/**
 * WHOSE queued entry a drain may send. Its own module, with NO imports, for two reasons: the
 * rule decides clinical PROVENANCE and deserves a name, and living in `queue.ts` put it behind
 * that file's `idb` and API-client imports — so a node-side test of the rule dragged the whole
 * browser API surface into a DOM-less typecheck.
 *
 * The server stamps `recorded_by` from the ACTOR, so draining user A's taps under user B's
 * token attributes A's observations to B. On a shared clinic tablet that is a corrupted
 * record, not a failed upload. Legacy entries carry no `userId` and drain to whoever is signed
 * in — refusing them would strand data with no other route out. With nobody known (auth still
 * loading) only those legacy entries qualify: an unknown viewer never claims a named person's.
 */
export function isDrainableBy(entry: { readonly userId?: string | undefined }, userId?: string): boolean {
  return entry.userId === undefined || entry.userId === userId;
}

/**
 * An entry whose SESSION has been discarded, caught mid-POST.
 *
 * Discarding a session deletes its recorded data server-side, and the local queue is purged
 * with it so nothing retries into a discarded encounter. An entry already in flight cannot be
 * deleted — the POST is out — so the purge TOMBSTONES it instead, and the drain honours that
 * when the request settles: whichever way it lands, the entry goes.
 *
 * Without this the entry survived the purge invisibly, and its retry into a discarded
 * encounter failed forever — a permanently errored row in the person's queue, for data that
 * was deliberately destroyed.
 */
export function isAbandoned(entry: { readonly abandoned?: boolean | undefined }): boolean {
  return entry.abandoned === true;
}

/** All three rules: whose entry it is, and whether it still has anywhere to go. */
export function isSendable(
  entry: {
    readonly userId?: string | undefined;
    readonly abandoned?: boolean | undefined;
    readonly status?: string | undefined;
  },
  userId?: string,
): boolean {
  return isDrainableBy(entry, userId) && !isAbandoned(entry) && !isRejected(entry);
}


/**
 * A refusal the server will repeat for ever — named, never inferred from the status class.
 *
 * Classifying by STATUS CLASS discards on sight: DPA_REQUIRED, MFA_REQUIRED, CASE_NOT_ACTIVE,
 * TUTOR_ACCESS_REQUIRED and the 402 limits all clear and the replay then lands. Worst is
 * PROCESSING_RESTRICTED — Art. 18 means the data MAY BE STORED, so treating it as final
 * destroys exactly what the restriction preserves, and nothing replays when it lifts.
 *
 * So the set is an ALLOWLIST and the default is RETRY: a denial code added later is retryable
 * until someone decides otherwise, which fails towards keeping clinical data.
 */
const FINAL_CODES: ReadonlySet<string> = new Set([
  // The reading is of a type the target no longer has — re-record, never replay.
  'DATA_TYPE_MISMATCH',
  // The behaviour event's shape is not the measurement its target now reads.
  'MEASUREMENT_SHAPE_MISMATCH',
  // Malformed for its own declared type: on these routes the check is over the BYTES the
  // entry already carries (value_json against its own dataType), so a replay cannot differ.
  'VALIDATION_ERROR',
]);
/*
 * NOT_FOUND is deliberately ABSENT. It answers a deleted target AND a lost VISIBILITY — a
 * member removed and re-added, a support worker whose tutor access lapsed — and the entry
 * cannot tell which. Discarding a recoverable reading is unrecoverable; a genuinely dead one
 * retries but stays visible with its × control, so the reversible failure is the one to take.
 */

export function isPermanentRefusal(status: number, code?: string): boolean {
  if (status === 401 || status === 408 || status === 429) return false;
  if (status < 400 || status >= 500) return false;
  return code !== undefined && FINAL_CODES.has(code);
}

/** An entry the server has refused for good: kept and shown, never replayed. */
export function isRejected(entry: { readonly status?: string | undefined }): boolean {
  return entry.status === 'rejected';
}

type QueuedForCount = {
  readonly userId?: string | undefined;
  readonly status?: string | undefined;
  readonly idempotencyKey: string;
};

/** The counts a viewer is shown: only their own entries (`isDrainableBy`). */
export function queueCounts(entries: readonly QueuedForCount[], viewer: string | undefined): {
  pending: number; errored: number; rejected: number; inflightKeys: string[];
} {
  const mine = entries.filter((e) => isDrainableBy(e, viewer));
  return {
    pending: mine.filter((e) => e.status !== 'error' && e.status !== 'rejected').length,
    errored: mine.filter((e) => e.status === 'error').length,
    rejected: mine.filter((e) => e.status === 'rejected').length,
    inflightKeys: mine.filter((e) => e.status === 'inflight').map((e) => e.idempotencyKey),
  };
}

/**
 * What holds a session open, split by whose it is and whether it can still be sent. Another
 * person's entries still hold it (completing would strand them), but only they can act on them.
 */
export function completionBlockers(
  sessionEntries: readonly { readonly userId?: string | undefined; readonly status?: string | undefined }[],
  viewer: string | undefined,
): { ownRefused: number; ownUnsent: number; othersRefused: number; othersUnsent: number } {
  const out = { ownRefused: 0, ownUnsent: 0, othersRefused: 0, othersUnsent: 0 };
  for (const e of sessionEntries) {
    const own = isDrainableBy(e, viewer);
    const refused = isRejected(e);
    if (own && refused) out.ownRefused += 1;
    else if (own) out.ownUnsent += 1;
    else if (refused) out.othersRefused += 1;
    else out.othersUnsent += 1;
  }
  return out;
}

/** Whether the signed-in person may remove a queued entry: theirs, and not mid-send. */
export function removalDecision(
  entry: { readonly userId?: string | undefined; readonly status?: string | undefined } | undefined,
  viewer: string | undefined,
): 'absent' | 'not_yours' | 'inflight' | 'removable' {
  if (entry === undefined) return 'absent';
  if (!isDrainableBy(entry, viewer)) return 'not_yours';
  return entry.status === 'inflight' ? 'inflight' : 'removable';
}

/** The tab-held session log belongs to one person; with no person known, nothing is kept. */
export function sessionLogKey(encounterId: string, userId: string | null | undefined): string | null {
  return userId ? `hapmetrix-session-log-${encounterId}-${userId}` : null;
}
